// src/utils/apiClient.js

/**
 * API Client for communicating with backend services
 * Organized by AWS API Gateway endpoints
 */

// API Base URLs - can be configured via .env
const USER_MANAGEMENT_API = import.meta.env.VITE_USER_MANAGEMENT_API || 'https://vh99hz2mch.execute-api.eu-north-1.amazonaws.com';
const ORG_PROP_API = import.meta.env.VITE_ORG_PROP_API || 'https://47hb1v6d8k.execute-api.eu-north-1.amazonaws.com';
const HOST_MONITORING_API = import.meta.env.VITE_HOST_MONITORING_API || 'https://rdcii316h3.execute-api.eu-north-1.amazonaws.com';

/**
 * Generic API call helper
 * @param {string} baseUrl - API base URL
 * @param {string} endpoint - API endpoint path
 * @param {object} options - Fetch options
 * @param {string} token - JWT access token
 * @param {boolean} useHttpApi - If true, adds token as query param (for HTTP APIs)
 */
async function apiCall(baseUrl, endpoint, options = {}, token, useHttpApi = false) {
  if (!token) {
    throw new Error('Authentication token is required');
  }

  let url = `${baseUrl}${endpoint}`;
  
  // For HTTP APIs, add token as query parameter
  if (useHttpApi) {
    const separator = endpoint.includes('?') ? '&' : '?';
    url = `${url}${separator}token=${encodeURIComponent(token)}`;
  }
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // For REST APIs, add Authorization header
  if (!useHttpApi) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
    }

    // Parse and return JSON
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API call failed for ${url}:`, error);
    throw error;
  }
}

/**
 * Generic function to fetch data via presigned URL
 * Supports multiple data paths (lst_usr, host_monitoring, etc.)
 * 
 * @param {string} token - Auth token
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string|null} orgHash - Organization hash (for superadmins)
 * @param {string} path - Data path (e.g., 'lst_usr', 'host_monitoring')
 * @returns {Promise<Object>} - { data, count, date, path, file_size_mb }
 */
async function fetchDataViaPresignedUrl(token, date, orgHash = null, path = 'lst_usr') {
  if (!date) {
    throw new Error('Date parameter is required');
  }
  
  const params = new URLSearchParams();
  params.append('date', date);
  if (orgHash) params.append('org', orgHash);
  params.append('path', path);
  
  const endpoint = `/dlstusr?${params.toString()}`;
  
  console.log(`Fetching ${path} data for date: ${date}`);
  
  // Step 1: Get presigned URL from Lambda
  const response = await apiCall(USER_MANAGEMENT_API, endpoint, { method: 'GET' }, token);
  
  // Check if data is still being processed
  if (response.status === 'processing') {
    throw new Error(`Data is being processed. Please try again in ${response.retry_after_seconds} seconds.`);
  }
  
  console.log(`Downloading ${path} data from S3 (${response.file_size_mb} MB)...`);
  
  // Step 2: Download JSON from S3 using presigned URL
  const dataResponse = await fetch(response.download_url);
  
  if (!dataResponse.ok) {
    throw new Error(`Failed to download data: ${dataResponse.status}`);
  }
  
  const data = await dataResponse.json();
  
  console.log(`✅ Downloaded ${data.length} rows from ${path}`);
  
  return {
    data: data,
    count: data.length,
    date: date,
    path: path,
    file_size_mb: response.file_size_mb
  };
}

/**
 * UserManagement API
 * Handles user list data, metadata, and user operations
 */
export const userManagementAPI = {
  /**
   * Get metadata (dropdowns and dates)
   * GET /mlstusr?date=YYYY-MM-DD&org=hash (optional)
   */
  getMetadata: async (token, date = null, orgHash = null) => {
    let endpoint = '/mlstusr';
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (orgHash) params.append('org', orgHash);
    if (params.toString()) endpoint += `?${params.toString()}`;
    
    return apiCall(USER_MANAGEMENT_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Get user data for table (lst_usr) - PRESIGNED URL VERSION
   * GET /dlstusr?date=YYYY-MM-DD&org=hash&path=lst_usr
   */
  getUserData: async (token, date, orgHash = null) => {
    return fetchDataViaPresignedUrl(token, date, orgHash, 'usr_lst/data');
  },

  /**
   * Get host monitoring data - PRESIGNED URL VERSION
   * GET /dlstusr?date=YYYY-MM-DD&org=hash&path=host_monitoring
   */
  getHostMonitoring: async (token, date, orgHash = null) => {
    return fetchDataViaPresignedUrl(token, date, orgHash, 'host_monitoring');
  },

  /**
   * Get pending actions from mkrm_usr_backlog
   * GET /mkrm_usr_backlog?org={org}
   */
  getPendingActions: async (token, org) => {
    const response = await fetch(
      `https://vh99hz2mch.execute-api.eu-north-1.amazonaws.com/mkrm_usr_backlog?org=${org}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch pending actions: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Remove single pending action from waitlist
   * DELETE /remove?org={org}&email_property={email#property}
   */
  removePendingAction: async (token, org, emailProperty) => {
    // URL encode the email_property (contains #)
    const encodedEmailProperty = encodeURIComponent(emailProperty);
    
    const response = await fetch(
      `https://vh99hz2mch.execute-api.eu-north-1.amazonaws.com/remove?org=${org}&email_property=${encodedEmailProperty}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to remove pending action: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Generic function to fetch any data path
   * 
   * @param {string} token - Auth token
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} path - Data path (e.g., 'lst_usr', 'host_monitoring')
   * @param {string|null} orgHash - Organization hash (optional)
   */
  getData: async (token, date, path, orgHash = null) => {
    return fetchDataViaPresignedUrl(token, date, orgHash, path);
  },

  /**
   * Add 'add' and 'remove' records to mrkm_usr_backlog
   * POST /add
   */
  addUsers: async (token, data) => {
    return apiCall(USER_MANAGEMENT_API, '/add', {
      method: 'POST',
      body: JSON.stringify({ data }),
    }, token);
  },

  /**
   * Get execution log from presigned URL
   * GET /mkrm_usr_log?org={org}
   */
  getExecutionLog: async (token, org) => {
    try {
      // Step 1: Get presigned URL from Lambda
      const response = await fetch(
        `https://vh99hz2mch.execute-api.eu-north-1.amazonaws.com/mkrm_usr_log?org=${org}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get execution log URL: ${response.statusText}`);
      }
      
      const urlData = await response.json();
      
      // Check if there's no data yet
      if (urlData.status === 'no_data') {
        console.log('No execution log available yet');
        return { 
          data: [],
          count: 0,
          file_size_mb: 0
        };
      }
      
      console.log(`Downloading execution log from S3 (${urlData.file_size_mb} MB)...`);
      
      // Step 2: Download JSON from S3 using presigned URL
      const dataResponse = await fetch(urlData.download_url);
      
      if (!dataResponse.ok) {
        throw new Error(`Failed to download execution log: ${dataResponse.status}`);
      }
      
      const data = await dataResponse.json();
      
      console.log(`✅ Downloaded ${data.length} execution log entries`);
      
      return {
        data: data,
        count: data.length,
        file_size_mb: urlData.file_size_mb,
        last_modified: urlData.last_modified
      };
      
    } catch (error) {
      console.error('Error fetching execution log:', error);
      // Return empty data on error instead of throwing
      return { 
        data: [],
        count: 0,
        file_size_mb: 0
      };
    }
  },

/**
 * Get user management errors via presigned URL
 * GET /err_add?org={org}
 * Displays: user, property, action, permissions, issue, requestor, timestamp
 */
 getMkrmErrors: async (token, org) => {
  try {
    console.log(`Fetching user management errors for org: ${org}`);
    
    // Step 1: Get presigned URL from Lambda
    const response = await fetch(
      `https://vh99hz2mch.execute-api.eu-north-1.amazonaws.com/err_add?org=${org}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get error log URL: ${response.statusText}`);
    }
    
    const urlData = await response.json();
    
    // Check if there's no data yet
    if (urlData.status === 'no_data') {
      console.log('No user management errors available');
      return { 
        data: [],
        count: 0,
        file_size_mb: 0
      };
    }
    
    console.log(`Downloading error log from S3 (${urlData.file_size_mb} MB)...`);
    
    // Step 2: Download JSON from S3 using presigned URL
    const dataResponse = await fetch(urlData.download_url);
    
    if (!dataResponse.ok) {
      throw new Error(`Failed to download error log: ${dataResponse.status}`);
    }
    
    const data = await dataResponse.json();
    
    console.log(`✅ Downloaded ${data.length} error entries`);
    
    return {
      data: data,
      count: data.length,
      file_size_mb: urlData.file_size_mb,
      last_modified: urlData.last_modified
    };
    
  } catch (error) {
    console.error('Error fetching mkrm errors:', error);
    return { 
      data: [],
      count: 0,
      file_size_mb: 0
    };
  }
},

/**
 * Get access errors via presigned URL
 * GET /err_lst?org={org}&date={date}
 * If no date provided, uses latest from dates.csv
 * Displays: date, property, issue
 */
getAccessErrors: async (token, org, date = null) => {
  try {
    console.log(`Fetching access errors for org: ${org}${date ? `, date: ${date}` : ' (latest)'}`);
    
    // Step 1: Get presigned URL from Lambda
    const url = date 
      ? `https://vh99hz2mch.execute-api.eu-north-1.amazonaws.com/err_lst?org=${org}&date=${date}`
      : `https://vh99hz2mch.execute-api.eu-north-1.amazonaws.com/err_lst?org=${org}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get access error URL: ${response.statusText}`);
    }
    
    const urlData = await response.json();
    
    // Check if there's no data yet
    if (urlData.status === 'no_data') {
      console.log('No access errors for this date');
      return { 
        data: [],
        count: 0,
        file_size_mb: 0,
        date_used: date || 'unknown'
      };
    }
    
    console.log(`Downloading access errors from S3 (${urlData.file_size_mb} MB)...`);
    
    // Step 2: Download JSON from S3 using presigned URL
    const dataResponse = await fetch(urlData.download_url);
    
    if (!dataResponse.ok) {
      throw new Error(`Failed to download access errors: ${dataResponse.status}`);
    }
    
    const data = await dataResponse.json();
    
    console.log(`✅ Downloaded ${data.length} access error entries for date: ${urlData.date_used}`);
    
    return {
      data: data,
      count: data.length,
      file_size_mb: urlData.file_size_mb,
      last_modified: urlData.last_modified,
      date_used: urlData.date_used
    };
    
  } catch (error) {
    console.error('Error fetching access errors:', error);
    return { 
      data: [],
      count: 0,
      file_size_mb: 0,
      date_used: date || 'unknown'
    };
  }
}
};

/**
 * Host Monitoring API
 * Handles host monitoring data, dates, and known host flags
 * Uses HTTP API (token as query parameter)
 */
export const hostMonitoringAPI = {
  /**
   * Get available dates from dates.csv
   * GET /dates?customer_hash={hash}&token={token}
   */
  getDates: async (token, customerHash) => {
    const params = new URLSearchParams();
    params.append('customer_hash', customerHash);
    
    const endpoint = `/dates?${params.toString()}`;
    return apiCall(HOST_MONITORING_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Get host monitoring data via presigned URL
   * GET /lst?customer_hash={hash}&date={date}&token={token}
   */
  getHostData: async (token, customerHash, date) => {
    const params = new URLSearchParams();
    params.append('customer_hash', customerHash);
    params.append('date', date);
    
    const endpoint = `/lst?${params.toString()}`;
    
    console.log(`Fetching host monitoring data for date: ${date}`);
    
    // Step 1: Get presigned URL from Lambda
    const response = await apiCall(HOST_MONITORING_API, endpoint, { method: 'GET' }, token);
    
    console.log(`Downloading host data from S3 (${response.file_size_mb} MB)...`);
    
    // Step 2: Download JSON from S3 using presigned URL
    const dataResponse = await fetch(response.download_url);
    
    if (!dataResponse.ok) {
      throw new Error(`Failed to download host data: ${dataResponse.status}`);
    }
    
    const data = await dataResponse.json();
    
    console.log(`✅ Downloaded ${data.length} hosts`);
    
    return {
      data: data,
      count: data.length,
      date: date,
      file_size_mb: response.file_size_mb,
      last_modified: response.last_modified
    };
  },

  /**
   * Get known hosts status (batch check)
   * GET /toggl_status?customer_hash={hash}&token={token}
   */
  getKnownHosts: async (token, customerHash) => {
    const params = new URLSearchParams();
    params.append('customer_hash', customerHash);
    
    const endpoint = `/toggl_status?${params.toString()}`;
    return apiCall(HOST_MONITORING_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Toggle known status for a host
   * POST /toggl (token added as query parameter)
   */
  toggleKnown: async (token, customerHash, host, domain, known) => {
    const endpoint = '/toggl';
    return apiCall(HOST_MONITORING_API, endpoint, {
      method: 'POST',
      body: JSON.stringify({
        customer_hash: customerHash,
        host: host,
        domain: domain,
        known: known
      }),
    }, token);
  }
};


/**
 * ORGProp API
 * Handles organization and user role information
 */
 export const orgPropAPI = {
  /**
   * Get organization and role for a user (legacy endpoint)
   * GET /user/{userID}
   */
  getOrgProp: async (token, userId) => {
    const endpoint = `/user/${userId}`;
    return apiCall(ORG_PROP_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Get full auth info including available orgs for superadmins
   * GET /auth/role/{userID}
   */
  getAuthRole: async (token, userId) => {
    const endpoint = `/auth/role/${userId}`;
    return apiCall(ORG_PROP_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Update user preferences
   * POST /user/{userID}/preferences
   * @param {string} token - Auth token
   * @param {string} userId - User ID (Cognito sub)
   * @param {object} preferences - Preferences to update (e.g., { rogue_monitoring_allowed: true })
   * @returns {Promise<object>} - Updated preferences
   */
   updateUserPreferences: async (token, userId, preferences) => {
    const endpoint = `/user/${userId}/preferences`;
    return apiCall(ORG_PROP_API, endpoint, {
      method: 'POST',
      body: JSON.stringify(preferences)
    }, token);
  },

  /**
   * Get organization profile (contract, limits)
   * GET /org-profile?org={org}
   */
  getOrgProfile: async (token, org) => {
    const endpoint = `/org-profile?org=${org}`;
    return apiCall(ORG_PROP_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Get organization statistics
   * GET /org-stats?org={org}
   */
  getOrgStats: async (token, org) => {
    const endpoint = `/org-stats?org=${org}`;
    return apiCall(ORG_PROP_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Get organization users
   * GET /org-users?org={org}
   */
  getOrgUsers: async (token, org) => {
    const endpoint = `/org-users?org=${org}`;
    return apiCall(ORG_PROP_API, endpoint, { method: 'GET' }, token);
  },

  /**
   * Add user to organization
   * POST /org-users
   */
  addOrgUser: async (token, data) => {
    return apiCall(ORG_PROP_API, '/org-users', {
      method: 'POST',
      body: JSON.stringify(data)
    }, token);
  },

  /**
   * Remove user from organization
   * DELETE /org-users?org={org}&userID={userID}
   */
  removeOrgUser: async (token, org, userID) => {
    const endpoint = `/org-users?org=${org}&userID=${userID}`;
    return apiCall(ORG_PROP_API, endpoint, { method: 'DELETE' }, token);
  },

};


// Default export with all APIs
export default {
  userManagement: userManagementAPI,
  orgProp: orgPropAPI,
  hostMonitoring: hostMonitoringAPI,
};




